const PLAYLIST_TYPE = "playlist";
const PARTNERS_NAME = "partners";

const isValidSection = (section) =>
  section.type !== PLAYLIST_TYPE &&
  section.name.toLowerCase() !== PARTNERS_NAME;

export function getAllSeasonsForSeries(videos, currentEpisodeId) {
  const playlistSection = videos.find(
    (section) =>
      section.type === PLAYLIST_TYPE &&
      section.name.toLowerCase() !== PARTNERS_NAME
  );

  if (!playlistSection) return null;

  const matchingSeries = playlistSection.series.find((series) =>
    series.seasons.some((season) =>
      season.episodes.some((episode) => episode.id === currentEpisodeId)
    )
  );

  return matchingSeries
    ? {
        seriesId: matchingSeries.id,
        seasons: matchingSeries.seasons.map(
          ({ seasonNumber, episodes = [] }) => ({
            seasonNumber,
            episodes
          })
        )
      }
    : null;
}

export function getAllSections(videos, currentEpisodeId, selectedSection) {
  const validSections = videos.filter(
    (section) =>
      isValidSection(section) &&
      (!selectedSection || section.key === selectedSection.key)
  );

  const matchingSection = validSections.find((section) =>
    section.videos.some((video) => video.id === currentEpisodeId)
  );

  return matchingSection
    ? {
        sectionId: matchingSection.id,
        episodes: matchingSection.videos || []
      }
    : null;
}

export const lastEpisodeInSeason = (season, episode) =>
  season.episodes[season.episodes.length - 1].id === episode.id;

export const nextSeasonOnSeries = (seasons, seasonIndex) =>
  seasonIndex + 1 < seasons.length ? seasons[seasonIndex + 1] : null;

export function getNextSection(videos, sectionId) {
  const filteredSections = videos.filter(isValidSection);
  const currentSectionIndex = filteredSections.findIndex(
    (section) => section.id === sectionId
  );

  if (
    currentSectionIndex === -1 ||
    currentSectionIndex >= filteredSections.length - 1
  ) {
    return null;
  }

  const nextSection = filteredSections[currentSectionIndex + 1];
  return {
    sectionId: nextSection.id,
    videos: nextSection.videos || []
  };
}

export function getNextEpisode(videos, currentEpisode, selectedSection) {
  if (!currentEpisode) {
    return { nextEpisode: null, isSeasonEnd: false, isSeriesEnd: false };
  }
  const isSeries = Boolean(currentEpisode.episodeNumber);

  if (isSeries) {
    return handleSeriesNextEpisode(videos, currentEpisode.id);
  }

  return handleSectionNextEpisode(videos, currentEpisode.id, selectedSection);
}

function handleSeriesNextEpisode(videos, currentEpisodeId) {
  const series = getAllSeasonsForSeries(videos, currentEpisodeId);
  if (!series)
    return { nextEpisode: null, isSeasonEnd: false, isSeriesEnd: false };

  const { seasons } = series;

  for (let seasonIndex = 0; seasonIndex < seasons.length; seasonIndex++) {
    const season = seasons[seasonIndex];
    const episodeIndex = season.episodes.findIndex(
      (ep) => ep.id === currentEpisodeId
    );

    if (episodeIndex === -1) continue;

    if (episodeIndex < season.episodes.length - 1) {
      return {
        nextEpisode: season.episodes[episodeIndex + 1],
        isSeasonEnd: false,
        isSeriesEnd: false
      };
    }

    const nextSeason = nextSeasonOnSeries(seasons, seasonIndex);
    return nextSeason
      ? {
          nextEpisode: nextSeason.episodes[0],
          isSeasonEnd: true,
          isSeriesEnd: false
        }
      : {
          nextEpisode: null,
          isSeasonEnd: true,
          isSeriesEnd: true
        };
  }

  return { nextEpisode: null, isSeasonEnd: false, isSeriesEnd: false };
}

function handleSectionNextEpisode(videos, currentEpisodeId, selectedSection) {
  const sections = getAllSections(videos, currentEpisodeId, selectedSection);
  if (!sections)
    return { nextEpisode: null, isSeasonEnd: false, isSeriesEnd: false };

  const { sectionId, episodes } = sections;
  const currentEpisodeIndex = episodes.findIndex(
    (ep) => ep.id === currentEpisodeId
  );

  if (currentEpisodeIndex < episodes.length - 1) {
    return {
      nextEpisode: episodes[currentEpisodeIndex + 1],
      isSeasonEnd: false,
      isSeriesEnd: false
    };
  }

  const nextSection = getNextSection(videos, sectionId);
  return nextSection
    ? {
        nextEpisode: nextSection.videos[0],
        isSeasonEnd: true,
        isSeriesEnd: false
      }
    : {
        nextEpisode: null,
        isSeasonEnd: true,
        isSeriesEnd: true
      };
}
